// 화면 18 — 45 종목 그리드 (3 섹터 × 15 row).
// 권위: ARCH_DASHBOARD §4.18, PRD_Dashboard §5.8.2.
// NEW_IN 종목은 카드 좌측에 강조 표지 (O-DB-9 cascade).

import { memo, useMemo } from 'react';
import { bandColor, type LeaderStockRow } from './types';

interface Props {
  rows: LeaderStockRow[];
  newInSymbols: ReadonlySet<string>;
  onSymbolClick: (symbol: string) => void;
  isLoading?: boolean;
}

interface SectorGroup {
  sector: string;
  rows: LeaderStockRow[];
}

export const LeaderStocksGrid = memo(function LeaderStocksGrid({
  rows,
  newInSymbols,
  onSymbolClick,
  isLoading,
}: Props): JSX.Element {
  const groups: SectorGroup[] = useMemo(() => {
    const map = new Map<string, LeaderStockRow[]>();
    for (const r of rows) {
      const arr = map.get(r.sector) ?? [];
      arr.push(r);
      map.set(r.sector, arr);
    }
    // 섹터 내 rank ASC.
    for (const [, arr] of map) {
      arr.sort((a, b) => a.rank_in_sector - b.rank_in_sector);
    }
    // 섹터 순서 — 응답에 등장한 순서 보존 (스냅샷이 sector × rank 순으로 내려옴).
    return Array.from(map.entries()).map(([sector, rs]) => ({ sector, rows: rs }));
  }, [rows]);

  if (isLoading && rows.length === 0) {
    return <div className="lst-empty">불러오는 중…</div>;
  }

  if (groups.length === 0) {
    return <div className="lst-empty">표시할 종목이 없습니다.</div>;
  }

  return (
    <div className="lst-grid" data-testid="lst-grid">
      {groups.map((g) => (
        <div key={g.sector} className="lst-sector-col" data-testid={`lst-sector-col-${g.sector}`}>
          <header className="lst-sector-col__header">
            <span className="lst-sector-col__name" title={g.sector}>
              {g.sector}
            </span>
            <span className="lst-sector-col__count">{g.rows.length}</span>
          </header>
          {g.rows.map((r) => (
            <StockCard
              key={r.symbol}
              row={r}
              isNewIn={newInSymbols.has(r.symbol)}
              onClick={() => onSymbolClick(r.symbol)}
            />
          ))}
        </div>
      ))}
    </div>
  );
});

interface StockCardProps {
  row: LeaderStockRow;
  isNewIn: boolean;
  onClick: () => void;
}

function StockCard({ row, isNewIn, onClick }: StockCardProps): JSX.Element {
  const bandLabel = row.band ?? '—';
  const bandBg = bandColor(row.band);
  const bandClass = `lst-card__band${row.band == null ? ' lst-card__band--null' : ''}`;
  const scoreText = row.final_score != null ? row.final_score.toFixed(2) : '—';

  return (
    <button
      type="button"
      className={`lst-card${isNewIn ? ' lst-card--new-in' : ''}`}
      onClick={onClick}
      title={`${row.name} (${row.symbol}) · ${row.sector} · rank ${row.rank_in_sector}`}
      data-testid={`lst-card-${row.symbol}`}
    >
      <span className="lst-card__rank">{row.rank_in_sector}</span>
      <span className="lst-card__name-col">
        <span className="lst-card__name">
          {row.name}
          {isNewIn && <span className="lst-newin-pill">NEW</span>}
        </span>
        <span className="lst-card__symbol">{row.symbol}</span>
      </span>
      <span className="lst-card__score">{scoreText}</span>
      <span
        className={bandClass}
        style={row.band == null ? undefined : { background: bandBg }}
        aria-label={`band ${bandLabel}`}
      >
        {bandLabel}
      </span>
    </button>
  );
}
